// ============================================
// Risk Radar — "Summarise it for me", Flow toxicity layer
//
// Same design as the Exposure summary (summary.ts): the button freezes the
// flow payload on screen, every figure is computed here by the page's own
// formatters, the AI only words the facts (the server rejects any number not
// in them), and the panel says what has moved since (driftOfFlow).
//
// The summary is of the platform on screen when the backend splits the flow
// by platform (fv.perPlatform), else of both platforms together. It covers
// both measures whatever the colour chosen:
//   toxic share — who the flow comes from (a label measure, not a loss);
//   markout     — whether the price then moved against the broker (the cost).
//
// Pure: no React, no fetching.
// ============================================

import { BAND_BY_ID, PLATFORM_LABEL, bandOf, fmtPct, fmtUsd, fmtUtcDateTime, type Band, type PlatformScope } from './model';
import { classLabel, fmtBps, fmtWindow, type FlowCost, type FlowSymbol, type FlowView } from './flowModel';

/** "a cost of $1.2K to the broker" / "$300 earned by the broker" / "not known". */
function costWords(v: number | null): string {
  if (v === null) return 'not known';
  return v > 0 ? `a cost of ${fmtUsd(v)} to the broker` : v < 0 ? `${fmtUsd(-v)} earned by the broker` : 'nothing either way';
}

/** The net cost and its parts, in words, for the facts. */
function costFact(c: FlowCost): string {
  const parts = [
    `price after the fills ${costWords(c.price)}`,
    c.basis === 'none' ? 'no IB rebates (no IB)' : `IB rebates ${c.rebate === null ? 'not known' : fmtUsd(c.rebate)}${c.basis === 'accrued' ? ' (accrued, worked out from the rebate rules)' : c.basis === 'paid' ? ' (paid)' : ''}`,
    c.clientRebate !== null && c.clientRebate !== 0 ? `rebates to clients ${fmtUsd(c.clientRebate)}` : null,
    `commission paid by clients ${c.commission === null ? 'not known' : fmtUsd(c.commission)}`,
    c.fee !== null && c.fee !== 0 ? `fees ${fmtUsd(c.fee)}` : c.fee === null ? 'fees not known' : null,
  ].filter(Boolean);
  const cover = c.coverage !== null && c.coverage < 0.995 ? ` Commission and rebates are known for ${fmtPct(c.coverage)} of the B-Book flow.` : '';
  return `net ${costWords(c.net)}${c.complete ? '' : ' (some parts not known)'}: ${parts.join(', ')}.${cover}`;
}
import type { Drift, FrozenSummary, KeyFigure, SummaryFact, SummaryText } from './summary';

/** What the moment looked like, kept to tell the reader what has moved since. */
export interface FlowFingerprint {
  bShare: number | null;
  shareBand: Band | null;
  markout5: number | null;
  markoutBand: Band | null;
  flaggedAccounts: number;
  top: string | null;
  bands: Record<string, Band | null>;
}

export interface FlowSummaryHelpers {
  /** The whole-book B-Book toxic share a few minutes before the moment, or null. */
  before: { share: number; mins: number } | null;
}

const TOP_N = 3;

function bandWord(b: Band | null): string {
  return b ? BAND_BY_ID[b].label.toUpperCase() : 'no band';
}

function share(v: number | null, digits = 1): string {
  return v === null ? '—' : fmtPct(v, digits);
}

/** "against the broker" / "the broker's way" for a markout in USD (positive = against the broker). */
function againstWords(usd: number | null): string {
  if (usd === null) return 'not measured';
  if (usd > 0) return `${fmtUsd(usd)} against the broker`;
  if (usd < 0) return `${fmtUsd(-usd)} in the broker's favour`;
  return 'nothing either way';
}

/** The book's markout band: a reading only when the price moved against the broker. */
function markoutBandOf(fv: FlowView, bps: number | null): Band | null {
  return bps === null ? null : bandOf(bps > 0 ? bps : 0, fv.markoutThresholds);
}

/** The toxic class with the most flow on an instrument, or null. */
function topToxicClass(s: FlowSymbol): { cls: string; flowUsd: number; accounts: number } | null {
  const c = s.byClass.find((x) => x.toxicClass && x.flowUsd > 0);
  return c ? { cls: c.cls, flowUsd: c.flowUsd, accounts: c.accounts } : null;
}

function markoutText(s: FlowSymbol): string {
  const bps = s.markout.bBps['5'];
  if (s.bFlowUsd <= 0) return 'no B-Book flow, so no B-Book markout';
  if (bps === null) return `B-Book markout at 5 s not measured (${s.markout.fills['5']} fills with a markout, too few)`;
  return `its own B-Book markout at 5 s ${fmtBps(bps)} (band ${bandWord(s.markoutBand)}), ${againstWords(s.markout.bUsd['5'])}, over all its B-Book clients, flagged or not`;
}

/** "MT5 only" / "cTrader only" / "MT5 and cTrader together", for what the figures cover. */
export function flowCoverText(fv: FlowView, scope: PlatformScope): string {
  return fv.perPlatform && scope !== 'ALL' ? `${PLATFORM_LABEL[scope]} only` : 'MT5 and cTrader together';
}

/**
 * @param fv     the frozen payload in the colour "Toxic share", measured with
 *               the platform on screen (its own figures when split), else "Both"
 * @param scope  the platform scope fv was built with
 */
export function freezeFlow(
  fv: FlowView,
  asOf: number,
  scope: PlatformScope,
  helpers: FlowSummaryHelpers,
): FrozenSummary<FlowFingerprint> {
  const cover = flowCoverText(fv, scope);
  const facts: SummaryFact[] = [];
  const add = (id: string, text: string) => facts.push({ id, text });
  const t = fv.totals;
  const win = fmtWindow(fv.windowSec);
  const m = t.markout;
  const mBps = m.bBps['5'];
  const mBand = markoutBandOf(fv, mBps);

  // ── Moment and what the measures mean ──
  add('time', `Snapshot summarised: ${fmtUtcDateTime(asOf)}. The figures cover client deals in the last ${win} before it, ${cover}. Valid for that moment only; the live page moves on.`);
  add('toxic_set', `Toxic, as set by the broker in Flow settings: ${fv.toxicClasses.length === 0
    ? 'no class, so nothing is flagged'
    : fv.toxicClasses.map((c) => `${classLabel(c.cls)}${c.minConfidence !== null ? ` at confidence ${c.minConfidence.toFixed(2)} or more` : ''}${c.minRiskLevel ? ` and risk ${c.minRiskLevel} or higher` : ''}`).join('; ')}.`);
  add('meaning', 'Toxic share is the share of the flow from accounts whose current classification is in the toxic set: it says who the flow comes from, not what it cost. An account is counted with its classification now, so a reclassification moves its whole window of flow at once. Markout says what it cost: how far the price moved the clients\' way after their fills; positive is against the broker.');

  // ── The book ──
  add('share', t.bShare === null
    ? `No B-Book flow in the last ${win}.`
    : `Toxic share of B-Book flow: ${share(t.bShare)}, band ${bandWord(fv.bookBand)}: ${fmtUsd(t.bFlaggedUsd)} from flagged accounts of ${fmtUsd(t.bFlowUsd)} B-Book flow. All books: ${share(t.share)} (${fmtUsd(t.flaggedUsd)} of ${fmtUsd(t.flowUsd)}).`);
  if (helpers.before && t.bShare !== null) {
    add('share_before', `Toxic share of B-Book flow ${helpers.before.mins} min earlier: ${share(helpers.before.share)}.`);
  }
  add('accounts', `Flagged accounts: ${t.flaggedAccounts} of ${t.accounts} trading. Not classified: ${t.unclassifiedAccounts} account${t.unclassifiedAccounts === 1 ? '' : 's'}, ${fmtUsd(t.unclassifiedUsd)} of flow; they are never flagged, so their flow counts as not toxic.`);
  if (fv.hasMarkout) {
    add('markout', mBps === null
      ? `B-Book markout at 5 s: not measured, ${m.fills['5']} fills with a markout${fv.minFills !== null ? `, ${fv.minFills} needed` : ''}.`
      : `Whole book, all instruments together: B-Book markout at 5 s ${fmtBps(mBps)} (band ${bandWord(mBand)}), ${againstWords(m.bUsd['5'])}, over ${m.fills['5']} fills. Of that, the flagged accounts' part: ${againstWords(m.flaggedUsd['5'])}.`);
    // Materiality: the bands say whether a markout is a real cost.
    const mt = fv.markoutThresholds;
    const above = fv.symbols.filter((s) => s.markoutBand && s.markoutBand !== 'low' && s.bFlowUsd > 0);
    add('materiality', above.length === 0 && (mBand === 'low' || mBand === null)
      ? `Markout bands at 5 s: Low under ${mt.warning} bps, Warning from ${mt.warning}, High from ${mt.high}, Critical from ${mt.critical} bps. The whole book and every instrument are in the Low band: the price has not cost the broker anything material in the window, so no instrument has a markout loss worth acting on.`
      : `Markout bands at 5 s: Low under ${mt.warning} bps, Warning from ${mt.warning}, High from ${mt.high}, Critical from ${mt.critical} bps. Above Low: ${above.map((s) => `${s.symbol} ${bandWord(s.markoutBand)}`).join(', ') || 'the whole book only'}.`);
  }
  const bc = fv.bandCounts;
  if (t.cost) {
    add('cost', `Cost to the broker of the B-Book flow, whole ${fv.perPlatform ? 'platform' : 'book'}, all costs in (price move after the fills with the spread paid, rebates, commission, fees): ${costFact(t.cost)}`);
  }
  add('count', `Instruments by toxic share band: Critical ${bc.critical}, High ${bc.high}, Warning ${bc.warning}, Low ${bc.low}${fv.noBFlow > 0 ? `; ${fv.noBFlow} with no B-Book flow` : ''}.`);

  // ── The instruments with the most toxic B-Book flow ──
  const ranked = fv.symbols.filter((s) => s.rank !== null);
  const top = ranked.slice(0, TOP_N);
  top.forEach((s, i) => {
    const cls = topToxicClass(s);
    const parts = [
      `${s.symbol}${i === 0 ? ' (most toxic B-Book flow)' : ''}: toxic share of B-Book flow ${share(s.bShare)}, band ${bandWord(s.band)}, ${fmtUsd(s.bFlaggedUsd)} of ${fmtUsd(s.bFlowUsd)}`,
      `${s.flaggedAccounts} flagged account${s.flaggedAccounts === 1 ? '' : 's'} of ${s.accounts}`,
      cls ? `mostly ${classLabel(cls.cls)} (${fmtUsd(cls.flowUsd)}, ${cls.accounts} account${cls.accounts === 1 ? '' : 's'})` : null,
      fv.hasMarkout ? markoutText(s) : null,
      s.unclassifiedShare !== null && s.unclassifiedShare >= 0.2 ? `${share(s.unclassifiedShare, 0)} of its flow is from accounts not classified` : null,
      s.cost ? `cost to the broker of its B-Book flow, all costs in: net ${costWords(s.cost.net)}` : null,
    ].filter(Boolean);
    add(`sym_${s.symbol}`, `${parts.join('; ')}.`);
  });
  const rest = ranked.slice(TOP_N);
  if (rest.length > 0) {
    const restUsd = rest.reduce((sum, s) => sum + s.bFlaggedUsd, 0);
    add('rest', `The other ${rest.length} instrument${rest.length === 1 ? '' : 's'} with toxic B-Book flow together: ${fmtUsd(restUsd)}.`);
  }

  // ── Where the price moved most against the broker (may differ from the above) ──
  let worst: FlowSymbol | null = null;
  if (fv.hasMarkout) {
    const against = (s: FlowSymbol) => (s.bFlowUsd > 0 && s.markout.bBps['5'] !== null ? Math.max(0, s.markout.bUsd['5'] ?? 0) : 0);
    for (const s of fv.symbols) if (against(s) > (worst ? against(worst) : 0)) worst = s;
    if (worst) {
      add('worst_markout', `Largest B-Book markout against the broker at 5 s: ${worst.symbol}, ${fmtBps(worst.markout.bBps['5'])}, ${againstWords(worst.markout.bUsd['5'])}.`);
    } else if (mBps !== null) {
      add('worst_markout', 'No instrument\'s B-Book markout at 5 s is against the broker.');
    }
  }

  // ── What the reader can open ──
  add('scope', fv.perPlatform && scope !== 'ALL'
    ? `Every figure here is ${cover}: the other platform's flow is not in them.`
    : scope === 'ALL'
      ? 'Every figure here is MT5 and cTrader together.'
      : `The page was showing the ${PLATFORM_LABEL[scope]} tab, but every figure here is MT5 and cTrader together: the feed does not split the flow by platform.`);
  add('pages', 'Pages the risk manager can open for review: the instrument\'s account list in Risk Radar (select the instrument), Traders (an account\'s classification), Flow settings (the toxic set and the bands). Nothing is executed from Risk Radar.');

  // ── Key figures ──
  const keyFigures: KeyFigure[] = [
    ...(t.cost ? [{
      label: 'Cost to the broker, B-Book',
      value: t.cost.net === null ? '—' : t.cost.net > 0 ? fmtUsd(t.cost.net) : `−${fmtUsd(-t.cost.net)}`,
      note: t.cost.net === null ? 'not known' : t.cost.net > 0 ? 'net cost, all costs in' : 'net: the broker earned',
    }] : []),
    {
      label: 'Toxic share of B-Book flow',
      value: share(t.bShare),
      note: t.bShare === null ? `no B-Book flow, last ${win}` : `${fmtUsd(t.bFlaggedUsd)} of ${fmtUsd(t.bFlowUsd)} · last ${win}`,
      band: fv.bookBand,
    },
    ...(fv.hasMarkout ? [{
      label: 'B-Book markout at 5 s',
      value: fmtBps(mBps),
      note: mBps === null ? 'too few fills' : againstWords(m.bUsd['5']),
      band: mBand,
    }] : []),
    {
      label: 'Flagged accounts',
      value: `${t.flaggedAccounts} of ${t.accounts}`,
      note: t.unclassifiedAccounts > 0 ? `${t.unclassifiedAccounts} not classified (${fmtUsd(t.unclassifiedUsd)})` : 'all classified',
    },
    ...top.map((s) => ({
      label: s.symbol,
      value: fmtUsd(s.bFlaggedUsd),
      note: `${share(s.bShare, 0)} ${bandWord(s.band).toLowerCase()}${fv.hasMarkout && s.markout.bBps['5'] !== null ? ` · 5 s ${fmtBps(s.markout.bBps['5'])}` : ''}`,
      band: s.band,
    })),
  ];

  const bands: Record<string, Band | null> = {};
  for (const s of top) bands[s.symbol] = s.band;

  return {
    asOf,
    takenAt: Date.now(),
    facts,
    keyFigures,
    fallback: fallbackText(fv, top, worst),
    fingerprint: {
      bShare: t.bShare,
      shareBand: fv.bookBand,
      markout5: mBps,
      markoutBand: mBand,
      flaggedAccounts: t.flaggedAccounts,
      top: top[0]?.symbol ?? null,
      bands,
    },
  };
}

function fallbackText(fv: FlowView, top: FlowSymbol[], worst: FlowSymbol | null): SummaryText {
  const t = fv.totals;
  const win = fmtWindow(fv.windowSec);
  const mBps = t.markout.bBps['5'];

  const headline = t.bShare === null
    ? `No B-Book flow in the last ${win}.`
    : `${share(t.bShare)} of the B-Book flow in the last ${win} came from flagged accounts (${bandWord(fv.bookBand)})${
      fv.hasMarkout && mBps !== null ? `; 5 s after the fills the price had moved ${fmtBps(mBps)}, ${againstWords(t.markout.bUsd['5'])}` : ''}.`;

  const points: string[] = [];
  const first = top[0];
  if (first) {
    const cls = topToxicClass(first);
    points.push(`${first.symbol} has the most toxic B-Book flow: ${fmtUsd(first.bFlaggedUsd)}, ${share(first.bShare)}${cls ? `, mostly ${classLabel(cls.cls)}` : ''}.`);
  }
  if (t.cost && t.cost.net !== null) {
    points.push(`All costs in (price after the fills, rebates, commission, fees), the B-Book flow was ${costWords(t.cost.net)}${t.cost.complete ? '' : ', with some parts not known'}.`);
  }
  const material = (b: Band | null) => b !== null && b !== 'low';
  const costly = fv.symbols.filter((s) => s.bFlowUsd > 0 && material(s.markoutBand));
  if (fv.hasMarkout && mBps !== null && !material(markoutBandOf(fv, mBps)) && costly.length === 0) {
    points.push(t.cost && t.cost.net !== null && t.cost.net > 0
      ? 'At 5 s every markout is in the Low band: the cost comes from rebates and fees, not from the price.'
      : 'At 5 s every markout is in the Low band: the price has not cost the broker anything material, whatever the toxic share says.');
  } else if (fv.hasMarkout && mBps !== null) {
    const fl = t.markout.flaggedUsd['5'];
    points.push(fl !== null && fl > 0
      ? `5 s after their fills the flagged accounts had ${fmtUsd(fl)} against the broker, on ${fmtUsd(t.flaggedUsd)} of their flow, all books${worst ? `; the price moved most against the broker on ${worst.symbol}` : ''}.`
      : `5 s after their fills the price had not moved against the broker on the flagged accounts' flow: the toxic share is not showing a price cost.`);
  }
  if (t.unclassifiedAccounts > 0 && t.flowUsd > 0 && t.unclassifiedUsd / t.flowUsd >= 0.1) {
    points.push(`${t.unclassifiedAccounts} account${t.unclassifiedAccounts === 1 ? ' is' : 's are'} not classified (${fmtUsd(t.unclassifiedUsd)}): the toxic share may read low.`);
  }

  const actions: string[] = [];
  if (first) actions.push(`Select ${first.symbol} and review its flagged accounts and their markout.`);
  if (worst && worst.symbol !== first?.symbol && material(worst.markoutBand)) actions.push(`Select ${worst.symbol}: its price moved most against the broker.`);
  if (t.unclassifiedAccounts > 0) actions.push('Check in Traders why some accounts are not classified.');
  if (fv.toxicClasses.length === 0) actions.push('Set the toxic classes in Flow settings.');

  return { headline, points: points.slice(0, 3), actions: actions.slice(0, 3) };
}

// ── What has moved since ─────────────────────────────────────

const DRIFT_PCT = 0.05;

/** @param now  the live payload measured with the scope "Both" and the colour "Toxic share" */
export function driftOfFlow(f: FlowFingerprint, now: FlowView): Drift {
  const lines: string[] = [];
  let stale = false;
  const t = now.totals;

  if (f.bShare !== t.bShare) {
    const a = f.bShare ?? 0, b = t.bShare ?? 0;
    if (Math.abs(b - a) >= 0.0005) lines.push(`Toxic share ${share(f.bShare)} → ${share(t.bShare)}`);
    const rel = a > 0 ? (b - a) / a : (b > 0 ? 1 : 0);
    if (Math.abs(rel) >= DRIFT_PCT || (f.bShare === null) !== (t.bShare === null)) stale = true;
  }
  if (now.bookBand !== f.shareBand) {
    lines.push(`Toxic share band ${bandWord(f.shareBand)} → ${bandWord(now.bookBand)}`);
    stale = true;
  }
  const mNow = t.markout.bBps['5'];
  if (mNow !== f.markout5 && fmtBps(mNow) !== fmtBps(f.markout5)) {
    lines.push(`Markout 5 s ${fmtBps(f.markout5)} → ${fmtBps(mNow)}`);
  }
  const mBandNow = markoutBandOf(now, mNow);
  if (mBandNow !== f.markoutBand) {
    lines.push(`Markout band ${bandWord(f.markoutBand)} → ${bandWord(mBandNow)}`);
    stale = true;
  }
  if (t.flaggedAccounts !== f.flaggedAccounts) {
    lines.push(`Flagged accounts ${f.flaggedAccounts} → ${t.flaggedAccounts}`);
    stale = true;
  }
  const nowTop = now.symbols.find((s) => s.rank === 1)?.symbol ?? null;
  if (nowTop !== f.top) {
    lines.push(`Most toxic ${f.top ?? 'none'} → ${nowTop ?? 'none'}`);
    stale = true;
  }
  for (const [sym, was] of Object.entries(f.bands)) {
    const is = now.bySymbol.get(sym)?.band ?? null;
    if (is !== was) {
      lines.push(`${sym} ${bandWord(was)} → ${bandWord(is)}`);
      stale = true;
    }
  }
  return { stale, lines };
}
